import { dev } from '$app/environment';
import { derived, get, writable } from 'svelte/store';

import { client as apiClient } from '$lib/api/client.gen';
import {
	browserDevLogin as devLogin,
	getUserRedirect,
	browserGoogleLogin as googleLogin,
	browserLogout as logout,
} from '$lib/api/sdk.gen';

import type { User } from '../api/types.gen';
import { safeLocalStorageGet, safeLocalStorageSet } from './local-storage';

export type { User };

const STORAGE_KEY = 'ds:user';

const initial = typeof window !== 'undefined' ? safeLocalStorageGet<User>(STORAGE_KEY) : null;

export const user = writable<User | null>(initial);
const loginRedirect = writable<string | null>(null);

// Persist changes to localStorage in the browser
user.subscribe((u) => {
	safeLocalStorageSet(STORAGE_KEY, u ?? null);
});

// Session changes in another tab must take effect here too. In particular,
// stores which gate writes on the active user must never continue a queued
// request under credentials that were cleared or switched elsewhere.
if (typeof window !== 'undefined') {
	window.addEventListener('storage', (event) => {
		if (event.key !== STORAGE_KEY || event.storageArea !== window.localStorage) return;
		try {
			user.set(event.newValue === null ? null : (JSON.parse(event.newValue) as User));
		} catch {
			user.set(null);
		}
	});
}

export const isSignedIn = derived(user, ($user) => $user !== null);

export function sanitizeRedirect(
	path: string | null | undefined,
	origin = window.location.origin,
): string | null {
	if (!path) return null;
	if (!path.startsWith('/')) return null;

	try {
		const url = new URL(path, origin);
		if (url.origin !== origin) return null;
		return url.pathname + url.search + url.hash;
	} catch {
		return null;
	}
}

export function consumeLoginUrl(): string {
	const redirectTo = get(loginRedirect);
	loginRedirect.set(null);
	if (!redirectTo) return '/';

	const searchParams = new URLSearchParams({ redirect: redirectTo });
	return `/?${searchParams.toString()}`;
}

export async function signInWithDevEmail(email: string) {
	if (!dev) {
		throw new Error('Dev email sign-in is not available in this environment');
	}

	const { response, data } = await devLogin({ body: { email } });
	if (!response?.ok || !data) {
		// TODO: Proper error handling
		throw new Error('Failed to sign in with dev email');
	}
	user.set(data.user);
}

export async function signInWithGoogle(idToken: string) {
	const { response, data } = await googleLogin({ body: { id_token: idToken } });
	if (!response?.ok || !data) {
		// TODO: Proper error handling
		throw new Error('Failed to sign in with Google');
	}
	user.set(data.user);
}

export async function signOut({
	redirectTo,
	wait = true,
	throwOnError = false,
}: {
	redirectTo?: string | null;
	wait?: boolean;
	throwOnError?: boolean;
} = {}) {
	try {
		if (wait) {
			await logout({ throwOnError: throwOnError });
		} else {
			logout();
		}
	} finally {
		loginRedirect.set(sanitizeRedirect(redirectTo));
		user.set(null);
	}
}

export async function refreshUser() {
	const currentUser = get(user);
	if (!currentUser) return;

	try {
		const { response, data } = await getUserRedirect();
		if (response?.ok && data) {
			user.set(data as User);
		}
	} catch (error) {
		console.error('Failed to refresh user:', error);
		// TODO: If refresh fails, perhaps sign out or leave as is
	}
}

export function useCredentialsIncludeRequestInterceptor() {
	// Enable cookies for all API requests
	const _ = apiClient.interceptors.request.use((r) => {
		return new Request(r, { credentials: 'include' });
	});
}

export default {
	user,
	isSignedIn,
	consumeLoginUrl,
	signInWithDevEmail,
	signInWithGoogle,
	signOut,
	useCredentialsIncludeRequestInterceptor,
};
